import React from "react";
import Modal from "../../components/layout/Modal";
import HBox from "../../components/layout/HBox";
import VBox from "../../components/layout/VBox";
import Botao from "../../components/Botao";
import Icon from "../../components/Icon";
import AlertBox from "../../components/layout/AlertBox";
import Badge from "../../components/layout/Badge";
import { FormDynamicParams } from "./FormDynamicParams";
import {
    mdiArrowLeft,
    mdiLoading,
    mdiPlay,
    mdiCheck,
    mdiAlert,
    mdiChevronRight,
    mdiRefresh,
} from "@mdi/js";
import axios from "axios";

// ─── types ───────────────────────────────────────────────────────────────────

type ActionMeta = {
    name: string;
    description?: string;
    readOnly?: boolean;
    inputs?: FieldMeta[];
    outputs?: FieldMeta[];
    /** Ação desabilitada nesta instalação do adapter (ex: DISABLED_ACTIONS do TASY). */
    disabled?: boolean;
};

type FieldMeta = {
    name: string;
    type: string;
    title: string;
    required?: boolean;
    sensitive?: boolean;
    defaultValue?: any;
    options?: Array<{ label: string; value: any }>;
};

export type IntegrationTestActionModalProps = {
    isOpen: boolean;
    onClose: () => void;
    integration: {
        id: number;
        name: string;
        template_key: string;
        template_version?: string | null;
        connected: boolean;
    } | null;
};

// ─── helpers ─────────────────────────────────────────────────────────────────

/**
 * Flatten the result object into rows for the results table.
 * Every array-valued key becomes one row per item (field[idx]); every other
 * key becomes a single row. An action can have more than one list output
 * (e.g. listarPedidosExames devolve `contatos` e `descartes`), então TODAS as
 * chaves precisam virar linha — não só a primeira que for array.
 */
function flattenResult(result: any): Array<{ field: string; value: string }> {
    if (result === null || result === undefined) return [];

    if (typeof result === "object" && !Array.isArray(result)) {
        const rows: Array<{ field: string; value: string }> = [];
        for (const [k, v] of Object.entries(result)) {
            if (Array.isArray(v)) {
                if (v.length === 0) {
                    rows.push({ field: k, value: "[] (vazio)" });
                    continue;
                }
                v.forEach((item, idx) => {
                    rows.push({
                        field: `${k}[${idx}]`,
                        value: typeof item === "object" ? JSON.stringify(item, null, 2) : String(item),
                    });
                });
                continue;
            }
            rows.push({
                field: k,
                value: v === null || v === undefined ? "—" : typeof v === "object" ? JSON.stringify(v, null, 2) : String(v),
            });
        }
        return rows;
    }

    if (Array.isArray(result)) {
        return result.map((item, idx) => ({
            field: String(idx),
            value: typeof item === "object" ? JSON.stringify(item, null, 2) : String(item),
        }));
    }

    return [{ field: "resultado", value: String(result) }];
}

function buildInitialFormValues(inputs?: FieldMeta[]): Record<string, any> {
    const values: Record<string, any> = {};
    (inputs ?? []).forEach((f) => {
        if (f.defaultValue !== undefined) {
            values[f.name] = f.defaultValue;
        }
    });
    return values;
}

// ─── component ───────────────────────────────────────────────────────────────

type Step = "list" | "form";

export const IntegrationTestActionModal: React.FC<IntegrationTestActionModalProps> = ({
    isOpen,
    onClose,
    integration,
}) => {
    const [actions, setActions] = React.useState<ActionMeta[]>([]);
    const [loadingMeta, setLoadingMeta] = React.useState(false);
    const [metaError, setMetaError] = React.useState<string | null>(null);

    const [step, setStep] = React.useState<Step>("list");
    const [selectedAction, setSelectedAction] = React.useState<ActionMeta | null>(null);
    const [formValues, setFormValues] = React.useState<Record<string, any>>({});
    const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});

    const [confirmingWrite, setConfirmingWrite] = React.useState(false);
    const [running, setRunning] = React.useState(false);
    const [runError, setRunError] = React.useState<string | null>(null);
    const [result, setResult] = React.useState<any>(null);

    // Fetch template meta when modal opens or integration changes
    React.useEffect(() => {
        if (!isOpen || !integration?.template_key) return;

        setStep("list");
        setSelectedAction(null);
        setFormValues({});
        setFieldErrors({});
        setConfirmingWrite(false);
        setRunError(null);
        setResult(null);
        setMetaError(null);
        setActions([]);
        setLoadingMeta(true);

        const versionSegment = integration.template_version
            ? `/${integration.template_version}`
            : "";

        axios
            .get(`/api/integrations/templates/${integration.template_key}${versionSegment}`, {
                params: { integrationId: integration.id },
                withCredentials: true,
            })
            .then((res) => {
                if (res.data?.ok && res.data?.result?.actions) {
                    setActions(res.data.result.actions);
                } else {
                    setMetaError("Não foi possível carregar as ações disponíveis.");
                }
            })
            .catch(() => {
                setMetaError("Erro ao buscar metadados do template.");
            })
            .finally(() => setLoadingMeta(false));
    }, [isOpen, integration?.id]);

    // ── handlers ──────────────────────────────────────────────────────────────

    const handleSelectAction = (action: ActionMeta) => {
        if (action.disabled) return;
        setSelectedAction(action);
        setFormValues(buildInitialFormValues(action.inputs));
        setFieldErrors({});
        setConfirmingWrite(false);
        setRunError(null);
        setResult(null);
        setStep("form");
    };

    const handleBack = () => {
        setStep("list");
        setSelectedAction(null);
        setConfirmingWrite(false);
        setRunError(null);
        setResult(null);
    };

    const validateForm = (): boolean => {
        if (!selectedAction?.inputs?.length) return true;
        const errors: Record<string, string> = {};
        selectedAction.inputs.forEach((f) => {
            if (f.required && (formValues[f.name] === undefined || formValues[f.name] === "")) {
                errors[f.name] = "Campo obrigatório";
            }
        });
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleExecute = async () => {
        if (!integration || !selectedAction) return;
        if (!validateForm()) return;

        // Require confirmation for write operations
        if (!selectedAction.readOnly && !confirmingWrite) {
            setConfirmingWrite(true);
            return;
        }

        setRunning(true);
        setRunError(null);
        setResult(null);
        setConfirmingWrite(false);

        try {
            const res = await axios.post(
                `/api/integrations/${integration.id}/action/${selectedAction.name}`,
                formValues,
                { withCredentials: true }
            );
            setResult(res.data?.result ?? res.data);
        } catch (err: any) {
            setRunError(
                err.response?.data?.details ||
                err.response?.data?.error ||
                err.message ||
                "Erro ao executar ação"
            );
        } finally {
            setRunning(false);
        }
    };

    const handleCancelConfirm = () => {
        setConfirmingWrite(false);
    };

    // ── render helpers ────────────────────────────────────────────────────────

    const renderActionList = () => {
        if (loadingMeta) {
            return (
                <HBox style={{ padding: "24px", justifyContent: "center" }}>
                    <Icon path={mdiLoading} size={1.2} spin color="var(--tc-color-primary)" />
                    <span style={{ marginLeft: 8 }}>Carregando ações...</span>
                </HBox>
            );
        }

        if (metaError) {
            return <AlertBox variant="danger">{metaError}</AlertBox>;
        }

        if (!actions.length) {
            return <AlertBox variant="info">Nenhuma ação disponível para esta integração.</AlertBox>;
        }

        return (
            <VBox gap="8px">
                {actions.map((action) => {
                    const readonly = !!action.readOnly;
                    const disabled = !!action.disabled;
                    return (
                        <div
                            key={action.name}
                            onClick={() => handleSelectAction(action)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "12px 16px",
                                border: "1px solid var(--tc-color-border)",
                                borderRadius: "8px",
                                cursor: disabled ? "not-allowed" : "pointer",
                                background: "var(--tc-color-surface)",
                                opacity: disabled ? 0.55 : 1,
                                transition: "background 0.15s",
                            }}
                            onMouseEnter={(e) => {
                                if (disabled) return;
                                (e.currentTarget as HTMLDivElement).style.background =
                                    "var(--tc-color-surface-hover, var(--tc-color-gray-100))";
                            }}
                            onMouseLeave={(e) =>
                            ((e.currentTarget as HTMLDivElement).style.background =
                                "var(--tc-color-surface)")
                            }
                        >
                            <VBox gap="2px" style={{ flex: 1 }}>
                                <HBox gap="8px" style={{ alignItems: "center" }}>
                                    <span style={{ fontWeight: 600, fontSize: "0.95rem" }}>
                                        {action.name}
                                    </span>
                                    <Badge variant={readonly ? "success" : "warning"}>
                                        {readonly ? "leitura" : "escrita"}
                                    </Badge>
                                    {disabled && (
                                        <Badge variant="secondary">desabilitada nesta instalação</Badge>
                                    )}
                                </HBox>
                                {action.description && (
                                    <span style={{ fontSize: "0.82rem", color: "var(--tc-color-text-secondary)" }}>
                                        {action.description}
                                    </span>
                                )}
                            </VBox>
                            {!disabled && (
                                <Icon path={mdiChevronRight} size={0.9} color="var(--tc-color-text-secondary)" />
                            )}
                        </div>
                    );
                })}
            </VBox>
        );
    };

    const renderInlineResult = () => {
        if (result === null || result === undefined) return null;
        const rows = flattenResult(result);

        return (
            <VBox gap="8px">
                <HBox gap="6px" style={{ alignItems: "center" }}>
                    <Icon path={mdiCheck} size={0.85} color="var(--tc-color-success)" />
                    <strong style={{ fontSize: "0.9rem" }}>Resultado</strong>
                </HBox>

                {rows.length === 0 ? (
                    <AlertBox variant="info">A ação foi executada e não retornou dados.</AlertBox>
                ) : (
                    <div style={{ overflowX: "auto", borderRadius: "6px", border: "1px solid var(--tc-color-border)" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
                            <thead>
                                <tr>
                                    <th style={{
                                        textAlign: "left", padding: "8px 12px",
                                        background: "var(--tc-color-table-header, var(--tc-color-gray-100))",
                                        borderBottom: "2px solid var(--tc-color-border)",
                                        fontWeight: 600, whiteSpace: "nowrap",
                                    }}>Campo</th>
                                    <th style={{
                                        textAlign: "left", padding: "8px 12px",
                                        background: "var(--tc-color-table-header, var(--tc-color-gray-100))",
                                        borderBottom: "2px solid var(--tc-color-border)",
                                        fontWeight: 600,
                                    }}>Valor</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row, idx) => (
                                    <tr key={idx} style={{
                                        background: idx % 2 === 0
                                            ? "var(--tc-color-surface)"
                                            : "var(--tc-color-table-row-alt, var(--tc-color-gray-50))",
                                    }}>
                                        <td style={{
                                            padding: "8px 12px",
                                            borderBottom: "1px solid var(--tc-color-border)",
                                            fontWeight: 500, whiteSpace: "nowrap", verticalAlign: "top",
                                        }}>{row.field}</td>
                                        <td style={{
                                            padding: "8px 12px",
                                            borderBottom: "1px solid var(--tc-color-border)",
                                            fontFamily: "monospace", wordBreak: "break-all", whiteSpace: "pre-wrap",
                                        }}>{row.value}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </VBox>
        );
    };

    const renderForm = () => {
        if (!selectedAction) return null;
        const readonly = !!selectedAction.readOnly;
        const hasInputs = (selectedAction.inputs?.length ?? 0) > 0;

        return (
            <VBox gap="16px">
                {/* Back + title */}
                <HBox gap="8px" style={{ alignItems: "center" }}>
                    <Botao variant="none-secondary" icon={mdiArrowLeft} onClick={handleBack} />
                    <VBox gap="2px">
                        <strong style={{ fontSize: "1rem" }}>{selectedAction.name}</strong>
                        {selectedAction.description && (
                            <span style={{ fontSize: "0.82rem", color: "var(--tc-color-text-secondary)" }}>
                                {selectedAction.description}
                            </span>
                        )}
                    </VBox>
                    <Badge variant={readonly ? "success" : "warning"} style={{ marginLeft: "auto" }}>
                        {readonly ? "leitura" : "escrita"}
                    </Badge>
                </HBox>

                {/* Inputs */}
                {hasInputs ? (
                    <FormDynamicParams
                        schema={selectedAction.inputs!}
                        values={formValues}
                        onChange={setFormValues}
                        errors={fieldErrors}
                    />
                ) : (
                    <AlertBox variant="info">
                        Esta ação não requer parâmetros de entrada.
                    </AlertBox>
                )}

                {/* Write warning */}
                {!readonly && confirmingWrite && (
                    <AlertBox variant="warning">
                        <HBox gap="8px" style={{ alignItems: "center" }}>
                            <Icon path={mdiAlert} size={1} />
                            <span>
                                <strong>Atenção:</strong> esta ação irá <strong>alterar dados de produção</strong>.
                                Clique em <em>Executar mesmo assim</em> para confirmar.
                            </span>
                        </HBox>
                    </AlertBox>
                )}

                {/* Run error */}
                {runError && (
                    <AlertBox variant="danger">
                        <strong>Erro:</strong> {runError}
                    </AlertBox>
                )}

                {/* Execute button */}
                <HBox gap="8px" style={{ justifyContent: "flex-end" }}>
                    {confirmingWrite && (
                        <Botao variant="outline-secondary" onClick={handleCancelConfirm}>
                            Cancelar
                        </Botao>
                    )}
                    <Botao
                        variant={confirmingWrite ? "warning" : "primary"}
                        icon={running ? mdiLoading : result !== null ? mdiRefresh : mdiPlay}
                        onClick={handleExecute}
                        disabled={running}
                    >
                        {running
                            ? "Executando..."
                            : confirmingWrite
                                ? "Executar mesmo assim"
                                : result !== null
                                    ? "Re-executar"
                                    : "Executar"}
                    </Botao>
                </HBox>

                {/* Result inline */}
                {renderInlineResult()}
            </VBox>
        );
    };



    const title = `Testar Ações — ${integration?.name ?? ""}`;

    return (
        <Modal
            size="lg"
            show={isOpen}
            title={title}
            onClose={onClose}
            hideControls
        >
            <div style={{ minHeight: "200px" }}>
                {step === "list" && renderActionList()}
                {step === "form" && renderForm()}
            </div>
        </Modal>
    );
};
